<script>
/**
 * KRX 금현물 시세 조회 — HQ 전용.
 *
 * 수신 서버가 코스콤 전문을 파싱해 적재한 것을 본다.
 *   · 현재가 — 앱이 읽는 표(krx.last_price)와 같은 것
 *   · 체결   — A301G. 영업일로 파티션돼 있어 날짜가 반드시 들어간다
 *   · 종목   — A001G. 하루 세 번 들어오고 회차마다 덮어쓴다
 *
 * 가격은 모두 원 단위 정수다. 정의서에는 Double 로 적혀 있지만 전문에 소수점이
 * 들어오지 않는다.
 */
import {
  getKrxMeta,
  getKrxTrades,
  getKrxIssues,
  getKrxLastPrices,
} from '@/api/krxMarket.js'

export default {
  name: 'KrxTradePage',

  data() {
    return {
      tab: 'last',
      search: {
        bizDate: '',
        isin: '',
      },
      lastPrices: [],
      trades: { items: [], total: 0, page: 1, size: 50, totalPages: 0 },
      issues: [],
      loading: false,
      errorMessage: '',
    }
  },

  computed: {
    /** 종목 선택 상자. 체결을 종목으로 좁힐 때 쓴다. */
    isinOptions() {
      const opts = this.issues.map((i) => ({
        title: `${i.nameShortKr || i.nameKr || i.isin} (${i.isin})`,
        value: i.isin,
      }))
      return [{ title: '전체 종목', value: '' }, ...opts]
    },
  },

  async mounted() {
    await this.loadMeta()
    await this.refresh()
  },

  methods: {
    async loadMeta() {
      try {
        const res = await getKrxMeta()
        if (res.data?.code === 0) {
          this.search.bizDate = res.data.data?.defaultBizDate || ''
        } else {
          this.errorMessage = res.data?.message || '초기 정보를 가져오지 못했습니다.'
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '초기 정보를 가져오지 못했습니다.'
      }
    },

    async refresh() {
      this.loading = true
      this.errorMessage = ''
      try {
        await Promise.all([this.fetchLastPrices(), this.fetchIssues(), this.fetchTrades(1)])
      } finally {
        this.loading = false
      }
    },

    async fetchLastPrices() {
      const res = await getKrxLastPrices()
      if (res.data?.code === 0) {
        this.lastPrices = res.data.data || []
      }
    },

    async fetchIssues() {
      const res = await getKrxIssues({ bizDate: this.search.bizDate || undefined })
      if (res.data?.code === 0) {
        this.issues = res.data.data || []
      }
    },

    async fetchTrades(page = 1) {
      const res = await getKrxTrades({
        bizDate: this.search.bizDate || undefined,
        isin: this.search.isin || undefined,
        page,
        size: this.trades.size,
      })
      if (res.data?.code === 0) {
        this.trades = { ...res.data.data, size: this.trades.size }
      } else {
        this.errorMessage = res.data?.message || '체결 조회에 실패했습니다.'
      }
    },

    formatNumber(v) {
      if (v === null || v === undefined) return '-'
      return Number(v).toLocaleString('ko-KR')
    },

    /** 매매처리시각은 마이크로초까지 온다. 화면에는 초까지만 보여준다. */
    formatTradeTime(t) {
      if (!t) return '-'
      return String(t).substring(0, 8)
    },

    formatDateTime(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleString('ko-KR', { hour12: false })
    },

    /** 전일 종가 대비. 상승은 빨강, 하락은 파랑 — 국내 거래소 관례다. */
    changeOf(row) {
      if (!row.prevClose || !row.price) return null
      const diff = row.price - row.prevClose
      const rate = (diff / row.prevClose) * 100
      return { diff, rate }
    },

    changeColor(change) {
      if (!change || change.diff === 0) return 'text-grey'
      return change.diff > 0 ? 'text-red-darken-2' : 'text-blue-darken-2'
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">KRX 금현물 시세</div>
        <div class="text-body-2 text-grey mt-1">현재가 · 체결 · 종목정보</div>
      </div>
      <v-spacer />
      <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
        <v-icon start>mdi-refresh</v-icon>새로고침
      </v-btn>
    </div>

    <!-- 검색 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.bizDate"
              type="date"
              label="영업일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:170px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-select
              v-model="search.isin"
              :items="isinOptions"
              label="종목"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:280px;"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <v-card rounded="lg" elevation="0" border>
      <v-tabs v-model="tab" color="primary" density="compact">
        <v-tab value="last">현재가</v-tab>
        <v-tab value="trade">체결 ({{ formatNumber(trades.total) }})</v-tab>
        <v-tab value="issue">종목 ({{ issues.length }})</v-tab>
      </v-tabs>
      <v-divider />

      <v-window v-model="tab">
        <!-- 현재가 -->
        <v-window-item value="last">
          <v-table density="compact">
            <thead>
              <tr>
                <th style="width:150px;">종목코드</th>
                <th>종목명</th>
                <th class="text-right">현재가</th>
                <th class="text-right">대비</th>
                <th class="text-right">시가</th>
                <th class="text-right">고가</th>
                <th class="text-right">저가</th>
                <th class="text-right">누적수량</th>
                <th style="width:100px;">체결시각</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in lastPrices" :key="p.isin">
                <td class="text-caption">{{ p.isin }}</td>
                <td class="text-body-2 font-weight-medium">{{ p.issueName || '-' }}</td>
                <td class="text-right font-weight-bold">{{ formatNumber(p.price) }}</td>
                <td class="text-right text-caption" :class="changeColor(changeOf(p))">
                  <template v-if="changeOf(p)">
                    {{ changeOf(p).diff > 0 ? '+' : '' }}{{ formatNumber(changeOf(p).diff) }}
                    ({{ changeOf(p).rate.toFixed(2) }}%)
                  </template>
                  <template v-else>-</template>
                </td>
                <td class="text-right text-caption">{{ formatNumber(p.openPrice) }}</td>
                <td class="text-right text-caption">{{ formatNumber(p.highPrice) }}</td>
                <td class="text-right text-caption">{{ formatNumber(p.lowPrice) }}</td>
                <td class="text-right text-caption">{{ formatNumber(p.cumVolume) }}</td>
                <td class="text-caption">{{ formatTradeTime(p.tradeTime) }}</td>
              </tr>
              <tr v-if="lastPrices.length === 0">
                <td colspan="9" class="text-center text-grey py-8">
                  아직 수신된 시세가 없습니다.
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-window-item>

        <!-- 체결 -->
        <v-window-item value="trade">
          <v-table density="compact">
            <thead>
              <tr>
                <th style="width:110px;">체결시각</th>
                <th>종목</th>
                <th class="text-right">체결가</th>
                <th class="text-right">수량</th>
                <th class="text-right">누적수량</th>
                <th style="width:70px;">구분</th>
                <th style="width:70px;">보드</th>
                <th style="width:90px;">세션</th>
                <th style="width:100px;">수신기</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t, i) in trades.items" :key="i">
                <td class="text-caption font-weight-medium">{{ formatTradeTime(t.tradeTime) }}</td>
                <td class="text-body-2">
                  {{ t.issueName || t.isin }}
                  <span class="text-caption text-grey ml-1">{{ t.isin }}</span>
                </td>
                <td class="text-right font-weight-bold">{{ formatNumber(t.price) }}</td>
                <td class="text-right text-caption">{{ formatNumber(t.volume) }}</td>
                <td class="text-right text-caption">{{ formatNumber(t.cumVolume) }}</td>
                <td class="text-caption">{{ t.lastSide || '-' }}</td>
                <td class="text-caption">{{ t.boardId }}</td>
                <td class="text-caption">{{ t.sessionId || '-' }}</td>
                <td class="text-caption text-grey">
                  {{ t.serverId }}
                  <v-chip v-if="t.source === 'RECOVERY'" size="x-small" color="warning" variant="flat">
                    복구
                  </v-chip>
                </td>
              </tr>
              <tr v-if="trades.items.length === 0">
                <td colspan="9" class="text-center text-grey py-8">
                  이 영업일의 체결이 없습니다.
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-divider v-if="trades.totalPages > 1" />
          <div v-if="trades.totalPages > 1" class="pa-3">
            <v-pagination
              :model-value="trades.page"
              :length="trades.totalPages"
              density="compact"
              total-visible="7"
              @update:model-value="fetchTrades"
            />
          </div>
        </v-window-item>

        <!-- 종목 -->
        <v-window-item value="issue">
          <v-table density="compact">
            <thead>
              <tr>
                <th style="width:150px;">표준코드</th>
                <th style="width:110px;">단축코드</th>
                <th>종목명</th>
                <th class="text-right">기준가</th>
                <th class="text-right">전일종가</th>
                <th class="text-right">상한가</th>
                <th class="text-right">하한가</th>
                <th style="width:90px;">거래정지</th>
                <th style="width:170px;">수신시각</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in issues" :key="i.isin">
                <td class="text-caption">{{ i.isin }}</td>
                <td class="text-caption">{{ i.shortCode || '-' }}</td>
                <td class="text-body-2 font-weight-medium">{{ i.nameShortKr || i.nameKr || '-' }}</td>
                <td class="text-right text-caption">{{ formatNumber(i.basePrice) }}</td>
                <td class="text-right text-caption">{{ formatNumber(i.prevClose) }}</td>
                <td class="text-right text-caption text-red-darken-2">{{ formatNumber(i.upperLimit) }}</td>
                <td class="text-right text-caption text-blue-darken-2">{{ formatNumber(i.lowerLimit) }}</td>
                <td>
                  <v-chip v-if="i.tradingHalt === 'Y'" color="error" size="x-small" variant="flat">
                    정지
                  </v-chip>
                  <span v-else class="text-caption text-grey">-</span>
                </td>
                <td class="text-caption text-grey">{{ formatDateTime(i.recvAt) }}</td>
              </tr>
              <tr v-if="issues.length === 0">
                <td colspan="9" class="text-center text-grey py-8">
                  이 영업일의 종목정보가 없습니다. A001G 는 장 시작 전 세 번 들어옵니다.
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-window-item>
      </v-window>
    </v-card>
  </div>
</template>
